// MACHINERY V2 (Stroje) — the fleet SHELL (M2.3; hoisted out of FleetV1Page).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.2 + M2.3. This is the `""`
// child of the v2 route.
//
// >>> WHY A SHELL EXISTS: ONE POLLER PER PAGE. <<<
// M2.2 put the data hook inside FleetV1Page. If M2.3 had done the same in
// FleetV2Page, the V1/V2 toggle would have owned two independent
// `useFleetTelemetry` instances — two 60 s intervals, two batch reads of
// /v1/gateway/machines, and a refetch on every toggle. So the hook is hoisted
// HERE and both views become BODIES that receive its output as props.
// `useFleetPageData` (and therefore `useFleetTelemetry`) is called exactly once
// on this surface. Do not call either from a body component.
//
// Everything else the two views share also lives here, for the same reason:
// filter rail, toolbar, search, scope, dialogs, the readiness popup and the
// toast host. Switching V1 <-> V2 re-renders a body and nothing else — no
// refetch, no reset of the user's filters, search or scope.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import usePermission from "../../../../shared/hooks/usePermission";
import { Modules } from "../../../../core/models/PermissionModels";
import { getMachineDisplayName } from "../../../../shared/utils/machineUtils";
import { Machine } from "../../models/models";
import { changeQuery, getMachines } from "../../customerMachinerySlice";
import AddMachinePicker from "../add/AddMachinePicker";
import MachineryToastHost from "../feedback/MachineryToastHost";
import ReadinessPopupV2 from "../readiness/ReadinessPopupV2";
import {
  MachineryV2FleetView,
  MachineryV2GroupBy,
  setFleetGroupBy,
  setFleetLocationFilter,
  setFleetView,
  toggleFleetStatusFilter,
  toggleRailCollapsed,
} from "../machineryV2UiSlice";
import FilterRail from "./FilterRail";
import { FilterNote, Notice } from "./FleetNotices";
import FleetDialogs from "./FleetDialogs";
import FleetToolbar, { FleetScope } from "./FleetToolbar";
import FleetV1Page from "./FleetV1Page";
import LiveFleetStrip from "./LiveFleetStrip";
import FleetV2Page from "./FleetV2Page";
import { fleetFetchQuery } from "./fleetFetchQuery";
import { useFleetDialogs } from "./useFleetDialogs";
import { useFleetHandlers } from "./useFleetHandlers";
import { useFleetSearch } from "./useFleetSearch";
import { useFleetPageData } from "./useFleetPageData";

const FleetPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation("myMachinery");
  // canAdmin gates ONE row: the decommissioned hard delete (MDEL-FE).
  const { canWrite, canAdmin } = usePermission(Modules.Machinery);

  const query = useAppSelector((s) => s.customerMachinery.query);
  const selectedMachine = useAppSelector((s) => s.customerMachinery.machine);
  const ui = useAppSelector((s) => s.machineryV2Ui);

  const [scope, setScope] = useState<FleetScope>("active");
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [readinessMachine, setReadinessMachine] = useState<Machine | null>(null);
  /** M2.7: the v2 add flow (picker -> custom form). See onAddMachine below. */
  const [addPickerOpen, setAddPickerOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // THE single data hook — and therefore the single telemetry poller.
  const data = useFleetPageData(scope);
  // The second argument is the catalog add form's way BACK to the picker
  // (M2.8) — the picker's open flag is this page's, so re-opening it is too.
  const dialogs = useFleetDialogs(query, () => setAddPickerOpen(true));
  // Debounced search + the always-current query ref (review F5).
  const { searchValue, onSearchChange, onSearchSubmit, queryRef } = useFleetSearch(query);
  // Named fields, not two adjacent booleans — a swap here used to type-check
  // clean and would have shown the hard delete to every Manager (review
  // finding 3).
  const { buildActions } = useFleetHandlers(
    { canWrite, canAdmin },
    queryRef,
    dialogs,
    navigate
  );

  // NEW badge (session-only), from ?newMachine= after a catalog-page redirect.
  useEffect(() => {
    const newId = searchParams.get("newMachine");
    if (!newId) return;
    dialogs.setNewMachineId(newId);
    searchParams.delete("newMachine");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Collapse state is keyed by the RAW section key and is shared between the
  // views — since ruling (i) both section from the SAME `fleetGroupBy` map, so
  // a group collapsed in V1 is the same group in V2. Reset on a group-by
  // change (see changeGroupBy).
  const toggleGroup = useCallback((name: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  // M2.4 repointed this from the LIVE profile to the v2 one. Card and row
  // clicks now stay on the v2 surface; the live profile is untouched and still
  // reachable at /machinery/:id until cutover (M2.12).
  const openProfile = useCallback(
    (m: Machine) => navigate(`/machinery-v2/${m.id}`),
    [navigate]
  );

  /**
   * REVIEW F2 — changing the dimension MUST clear the collapse set, exactly as
   * the live grid does (Machinery.tsx:759-761). The set is keyed by section
   * TITLE, and groupMachinesClientSide emits the same sentinel `"Ungrouped"`
   * for location, line AND department — so collapsing "Ungrouped" under
   * Group-by Line and switching to Group-by Location would render a DIFFERENT
   * section already collapsed with no user action. Any group name shared
   * across two dimensions collides the same way.
   */
  const changeGroupBy = useCallback(
    (g: MachineryV2GroupBy) => {
      dispatch(setFleetGroupBy(g));
      setCollapsedGroups(new Set());
    },
    [dispatch]
  );

  const readinessPresentation = readinessMachine
    ? data.readiness.get(readinessMachine.id)
    : undefined;

  const bodyProps = {
    states: data.states,
    telemetry: data.telemetry,
    readiness: data.readiness,
    newMachineId: dialogs.newMachineId,
    collapsedGroups,
    onToggleGroup: toggleGroup,
    buildActions,
    onOpenProfile: openProfile,
    onOpenReadiness: setReadinessMachine,
  };

  return (
    <div style={{ display: "flex", flex: 1, minHeight: 0, minWidth: 0 }}>
      <FilterRail
        collapsed={ui.railCollapsed}
        onToggleCollapsed={() => dispatch(toggleRailCollapsed())}
        stateCounts={data.stateCounts}
        selectedStates={ui.fleetStatusFilters}
        onToggleState={(s) => dispatch(toggleFleetStatusFilter(s))}
        types={data.types}
        typeCounts={data.typeCounts}
        selectedTypeId={query.typeId}
        onSelectType={(typeId) =>
          dispatch(changeQuery({ ...query, typeId, manufacturerId: undefined }))
        }
        manufacturers={data.manufacturers}
        selectedManufacturerId={query.manufacturerId}
        onSelectManufacturer={(manufacturerId) =>
          dispatch(changeQuery({ ...query, manufacturerId }))
        }
        locations={data.locations}
        selectedLocation={ui.fleetLocationFilter}
        onSelectLocation={(id) => dispatch(setFleetLocationFilter(id))}
        totalCount={data.flat.length}
      />

      <main
        style={{
          flex: 1, minWidth: 0, minHeight: 0, display: "flex",
          flexDirection: "column", overflow: "hidden",
        }}
      >
        <FleetToolbar
          search={searchValue}
          onSearchChange={onSearchChange}
          onSearchSubmit={onSearchSubmit}
          scope={scope}
          onScopeChange={setScope}
          activeCount={data.activeCount}
          decommissionedCount={data.decommissionedCount}
          view={ui.fleetView}
          onViewChange={(v: MachineryV2FleetView) => dispatch(setFleetView(v))}
          groupBy={ui.fleetGroupBy}
          onGroupByChange={changeGroupBy}
          canWrite={canWrite}
          onManageGroups={dialogs.openManageGroups}
          onAddMachine={() => setAddPickerOpen(true)}
        />

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 24px 24px" }}>
          {/* REVIEW F4 — status filters are telemetry-derived, and the
              decommissioned scope has no telemetry. Rather than silently
              matching nothing, say so. The filter is never cleared behind the
              user's back; switching back to Aktívne restores it. */}
          {data.statusFiltersIgnored && (
            <FilterNote text={translate("myMachinery:v2.fleet.statusFiltersIgnored")} />
          )}

          {/* REVIEW F1 — RENDERED ON ITS OWN TERMS, ABOVE THE BRANCH. Inside
              the loading/empty chain, toggling a status chip that matched
              nothing unmounted the very strip holding it, and any thunk that
              flips `loading` (createMachine / restoreMachine included) flashed
              it away mid-action. A filter control must outlive the result of
              its own filter. */}
          {scope === "active" && ui.fleetView === "V1" && (
            <LiveFleetStrip
              total={data.flat.length}
              connected={data.connectedCount}
              counts={data.stateCounts}
              selected={ui.fleetStatusFilters}
              onToggle={(s) => dispatch(toggleFleetStatusFilter(s))}
            />
          )}

          {data.isLoading ? (
            <Notice text={translate("myMachinery:v2.fleet.loading")} dashed={false} />
          ) : data.visibleCount === 0 ? (
            <Notice
              text={translate(
                scope === "decommissioned"
                  ? "myMachinery:noDecommissionedMachines"
                  : "myMachinery:noMachinesFound"
              )}
            />
          ) : ui.fleetView === "V2" ? (
            <FleetV2Page sections={data.sections} {...bodyProps} />
          ) : (
            <FleetV1Page sections={data.sections} {...bodyProps} />
          )}
        </div>
      </main>

      {/* Mounted at PAGE level, deliberately outside every card AND every row:
          the clickable ancestor is then not even a React ancestor of the popup,
          so a click on Close / the backdrop / a CTA cannot navigate.
          ReadinessPopupV2 ALSO carries the standing stopPropagation guard on
          BOTH Dialog.Overlay and Dialog.Content, which is what protects the
          in-card and in-row cases. */}
      {readinessMachine && readinessPresentation && (
        <ReadinessPopupV2
          open
          onOpenChange={(o) => {
            if (!o) setReadinessMachine(null);
          }}
          presentation={readinessPresentation}
          machineOrModelName={getMachineDisplayName(readinessMachine)}
          oemPartnerName={readinessMachine.catalogModel?.oemPartnerName}
          manufacturerName={readinessMachine.manufacturer?.name}
        />
      )}

      {/* M2.7 — the v2 add flow replaces MachineDialog's hub + custom form.
          The REFRESH lives here, not in the dialog: v1 fires getMachines from
          inside the dialog (MachineDialog.tsx:374), but every v2 mutation
          refresh goes through `fleetFetchQuery` (fleetFetchQuery.ts) and
          `queryRef` is the page's, not the dialog's. The dialog closes only
          after createMachine resolved (§9 D7), so this runs on success only. */}
      <AddMachinePicker
        open={addPickerOpen}
        onClose={() => setAddPickerOpen(false)}
        onCreated={(id) => {
          setAddPickerOpen(false);
          if (id) dialogs.setNewMachineId(id);
          dispatch(getMachines(fleetFetchQuery(queryRef.current)));
        }}
        onCatalogModel={dialogs.openCatalogAdd}
      />

      {/* The sanctioned v2 feedback primitive (review F2). */}
      <MachineryToastHost />

      <FleetDialogs selectedMachine={selectedMachine} {...dialogs.dialogProps} />
    </div>
  );
};

export default FleetPage;
